import React, { useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { billService } from '../services/billService';
import { useClient } from '../context/ClientContext';

const {
  FiCreditCard,
  FiSearch,
  FiRefreshCw,
  FiDollarSign,
  FiFileText,
  FiCalendar,
  FiArrowUpRight,
  FiAlertCircle,
  FiLoader
} = FiIcons;

const money = (amount, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(Number(amount) || 0);
  } catch {
    return `${currency} ${Number(amount || 0).toFixed(2)}`;
  }
};

const dateLabel = value => {
  if (!value) return '—';

  const date = new Date(`${value.slice(0, 10)}T12:00:00`);

  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
};

const methodLabel = value =>
  (value || 'other')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, char => char.toUpperCase());

const fieldClass =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

export default function PaymentsMade() {
  const { activeClient } = useClient();
  const organizationId = activeClient?.id;

  const [payments, setPayments] = useState([]);
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const loadData = async () => {
    if (!organizationId) return;

    setLoading(true);
    setError('');

    try {
      const [paymentRows, billRows, vendorRows] = await Promise.all([
        billService.getAllBillPayments(organizationId),
        billService.getBills(organizationId),
        billService.getVendors(organizationId)
      ]);

      setPayments(paymentRows);
      setBills(billRows);
      setVendors(vendorRows);
    } catch (err) {
      setError(err.message || 'Unable to load payments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    setPayments([]);
    setBills([]);
    setVendors([]);
    setError('');
    setLoading(Boolean(organizationId));

    if (!organizationId) return undefined;

    Promise.all([
      billService.getAllBillPayments(organizationId),
      billService.getBills(organizationId),
      billService.getVendors(organizationId)
    ])
      .then(([paymentRows, billRows, vendorRows]) => {
        if (cancelled) return;

        setPayments(paymentRows);
        setBills(billRows);
        setVendors(vendorRows);
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message || 'Unable to load payments.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const billMap = useMemo(
    () => Object.fromEntries(bills.map(bill => [bill.id, bill])),
    [bills]
  );

  const vendorMap = useMemo(
    () => Object.fromEntries(vendors.map(vendor => [vendor.id, vendor])),
    [vendors]
  );

  const enrichedPayments = useMemo(
    () =>
      payments.map(payment => {
        const bill = billMap[payment.bill_id];
        const vendor = bill ? vendorMap[bill.vendor_id] : null;

        return {
          ...payment,
          bill_number: bill?.bill_number || 'Unknown bill',
          vendor_name: vendor?.vendor_name || 'Unknown vendor',
          currency: bill?.currency || null
        };
      }),
    [payments, billMap, vendorMap]
  );

  const filteredPayments = useMemo(() => {
    const query = search.trim().toLowerCase();

    return enrichedPayments.filter(payment => {
      const matchesSearch =
        !query ||
        payment.bill_number.toLowerCase().includes(query) ||
        payment.vendor_name.toLowerCase().includes(query) ||
        (payment.reference_number || '').toLowerCase().includes(query);

      const matchesMethod =
        methodFilter === 'all' ||
        payment.payment_method === methodFilter;

      const matchesStart =
        !startDate || payment.payment_date >= startDate;

      const matchesEnd =
        !endDate || payment.payment_date <= endDate;

      return (
        matchesSearch &&
        matchesMethod &&
        matchesStart &&
        matchesEnd
      );
    });
  }, [
    enrichedPayments,
    search,
    methodFilter,
    startDate,
    endDate
  ]);

  const currencies = [
    ...new Set(filteredPayments.map(payment => payment.currency))
  ];

  const singleCurrency =
    currencies.length === 1 && currencies[0]
      ? currencies[0]
      : null;

  const totalPaid = filteredPayments.reduce(
    (sum, payment) => sum + Number(payment.amount || 0),
    0
  );

  const totalLabel =
    filteredPayments.length === 0
      ? money(0)
      : singleCurrency
        ? money(totalPaid, singleCurrency)
        : 'Multiple currencies';

  const uniqueBills = new Set(
    filteredPayments.map(payment => payment.bill_id)
  ).size;

  if (!organizationId) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <SafeIcon
          icon={FiCreditCard}
          className="mb-4 text-4xl text-slate-300"
        />
        <h2 className="text-lg font-semibold text-slate-800">
          Select a business
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Choose a business to view vendor payments.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl pb-24">
      <div className="mb-7 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Payments Made
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            View payments recorded against vendor bills.
          </p>
        </div>

        <button
          onClick={loadData}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          <SafeIcon
            icon={FiRefreshCw}
            className={loading ? 'animate-spin' : ''}
          />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <SafeIcon icon={FiAlertCircle} />
          {error}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        {[
          {
            title: 'Total payments',
            value: filteredPayments.length,
            icon: FiCreditCard
          },
          {
            title: 'Amount paid',
            value: totalLabel,
            icon: FiDollarSign
          },
          {
            title: 'Bills with payments',
            value: uniqueBills,
            icon: FiFileText
          }
        ].map(card => (
          <div
            key={card.title}
            className="rounded-2xl border border-slate-200 bg-white p-5"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">
                {card.title}
              </span>
              <SafeIcon
                icon={card.icon}
                size={18}
                className="text-slate-400"
              />
            </div>

            <p className="mt-3 text-2xl font-bold text-slate-900">
              {card.value}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Based on current filters
            </p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="grid grid-cols-1 gap-3 border-b border-slate-100 p-5 md:grid-cols-4">
          <div className="relative">
            <SafeIcon
              icon={FiSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              className={`${fieldClass} pl-10`}
              placeholder="Search payments..."
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </div>

          <select
            className={fieldClass}
            value={methodFilter}
            onChange={event => setMethodFilter(event.target.value)}
          >
            <option value="all">All payment methods</option>
            <option value="bank_transfer">Bank transfer</option>
            <option value="cash">Cash</option>
            <option value="check">Check</option>
            <option value="card">Card</option>
            <option value="other">Other</option>
          </select>

          <div className="relative">
            <SafeIcon
              icon={FiCalendar}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="date"
              className={`${fieldClass} pl-10`}
              value={startDate}
              onChange={event => setStartDate(event.target.value)}
              aria-label="Start date"
            />
          </div>

          <input
            type="date"
            className={fieldClass}
            value={endDate}
            onChange={event => setEndDate(event.target.value)}
            aria-label="End date"
          />
        </div>

        {loading ? (
          <div className="flex min-h-64 items-center justify-center gap-3 text-sm text-slate-500">
            <SafeIcon icon={FiLoader} className="animate-spin" />
            Loading payments...
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="flex min-h-72 flex-col items-center justify-center px-6 text-center">
            <SafeIcon
              icon={FiCreditCard}
              size={30}
              className="mb-4 text-slate-300"
            />
            <h3 className="font-semibold text-slate-900">
              No payments found
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Payments recorded from Bills will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead className="bg-slate-50 text-xs font-semibold text-slate-500">
                <tr>
                  <th className="px-6 py-4">Payment date</th>
                  <th className="px-5 py-4">Vendor</th>
                  <th className="px-5 py-4">Bill</th>
                  <th className="px-5 py-4">Method</th>
                  <th className="px-5 py-4">Reference</th>
                  <th className="px-5 py-4 text-right">Amount</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {filteredPayments.map(payment => (
                  <tr key={payment.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {dateLabel(payment.payment_date)}
                    </td>
                    <td className="px-5 py-4 text-sm font-medium text-slate-800">
                      {payment.vendor_name}
                    </td>
                    <td className="px-5 py-4 text-sm text-blue-600">
                      {payment.bill_number}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">
                      {methodLabel(payment.payment_method)}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-500">
                      {payment.reference_number || '—'}
                    </td>
                    <td className="px-5 py-4 text-right text-sm font-semibold text-slate-900">
                      {payment.currency
                        ? money(payment.amount, payment.currency)
                        : `${Number(payment.amount).toFixed(2)} (currency unavailable)`}
                    </td>
                    <td className="px-5 py-4 text-slate-400">
                      <SafeIcon icon={FiArrowUpRight} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="border-t border-slate-100 px-6 py-4 text-xs text-slate-500">
              Showing {filteredPayments.length} payments
            </div>
          </div>
        )}
      </div>
    </div>
  );
}